import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import { Accordion } from "PFComponents/accordion";
import { Inline, Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { Tile } from "PFComponents/tile";
import { Typography } from "PFComponents/typography";
import { SkillsFramework } from "PFCore/types";
import { useTranslation } from "react-i18next";

import { SkillsFrameworksNoSuggestions } from "./skills_frameworks_no_suggestions/skills_frameworks_no_suggestions";
import {
  SkillsFrameworksSuggestions,
  SkillsFrameworksSuggestionsProps
} from "./skills_frameworks_suggestions/skills_frameworks_suggestions";
import css from "./skills_frameworks_suggestions_box.module.scss";

type SkillsFrameworksFormSectionSuggestionsProps = Pick<
  SkillsFrameworksSuggestionsProps,
  "isDisabled" | "onSuggestionClick"
> & {
  isFetching?: boolean;
  primaryFrameworksSuggestions: SkillsFramework[];
  secondaryFrameworksSuggestions: SkillsFramework[];
};

export const SkillsFrameworksSuggestionsBox = ({
  isDisabled = false,
  isFetching = false,
  onSuggestionClick,
  primaryFrameworksSuggestions,
  secondaryFrameworksSuggestions
}: SkillsFrameworksFormSectionSuggestionsProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.skillsFrameworks" });

  if (isEmpty(primaryFrameworksSuggestions) && isEmpty(secondaryFrameworksSuggestions)) {
    return null;
  }

  return (
    <Tile
      className={classNames({ [css.isFetching]: isFetching })}
      paddingVariant="md"
      data-qa-id="SkillsFrameworks.suggestions"
    >
      <Accordion
        header={t("skillsFrameworksSuggestions")}
        headerVariant="h5"
        actions={isFetching && <LoadingDots />}
        animated={false}
      >
        <Stack gap="spacingLg">
          <Typography variant="bodyRegular">{t("skillsFrameworksSuggestionsInfo")}</Typography>
          <Inline gap="spacingLg" spread>
            <Stack className={css.suggestions}>
              <Typography variant="bodyBold">{t("primaryFrameworksSuggestionsHeader")}</Typography>
              {primaryFrameworksSuggestions.length > 0 ? (
                <SkillsFrameworksSuggestions
                  isDisabled={isDisabled && !isFetching}
                  suggestions={primaryFrameworksSuggestions}
                  onSuggestionClick={onSuggestionClick}
                />
              ) : (
                <SkillsFrameworksNoSuggestions />
              )}
            </Stack>
            <Stack className={css.suggestions}>
              <Typography variant="bodyBold">{t("secondaryFrameworksSuggestionsHeader")}</Typography>
              {secondaryFrameworksSuggestions.length > 0 ? (
                <SkillsFrameworksSuggestions
                  isDisabled={isDisabled && !isFetching}
                  suggestions={secondaryFrameworksSuggestions}
                  onSuggestionClick={onSuggestionClick}
                />
              ) : (
                <SkillsFrameworksNoSuggestions />
              )}
            </Stack>
          </Inline>
        </Stack>
      </Accordion>
    </Tile>
  );
};
